import { Check, X } from "lucide-react";
import React, { useEffect } from "react";

// Utility helper functions
const formatDate = (dateStr) => {
  if (!dateStr || dateStr === "-1" || dateStr === "") return "";
  try {
    const cleanDate = dateStr.split("+")[0];
    const d = new Date(cleanDate);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

const formatCurrency = (val) => {
  if (!val || val === "-1" || val === "") return "-";
  const num = Number(val);
  if (isNaN(num)) return val;
  return `₹ ${num.toLocaleString("en-IN")}`;
};

const getAccountType = (symbol) => {
  const map = {
    "01": "AUTO LOAN",
    "02": "HOUSING LOAN",
    "03": "PROPERTY LOAN",
    "04": "BUSINESS LOAN",
    "05": "PERSONAL LOAN",
    "06": "PERSONAL LOAN",
    10: "CREDIT CARD",
  };
  return map[symbol] || "PERSONAL LOAN";
};

const getOwnership = (symbol) => {
  const map = { 1: "INDIVIDUAL", 2: "JOINT", 3: "GUARANTOR" };
  return map[symbol] || "INDIVIDUAL";
};

const getFrequency = (symbol) => {
  const map = {
    "01": "WEEKLY",
    "02": "FORTNIGHTLY",
    "03": "MONTHLY",
    "04": "QUARTERLY",
  };
  return map[symbol] || "MONTHLY";
};

// Process monthly payment status into matrix grouped by Year
const processPaymentHistory = (monthlyList) => {
  if (!monthlyList || !Array.isArray(monthlyList))
    return { years: [], matrix: {} };

  const matrix = {};

  monthlyList.forEach((item) => {
    if (!item.date) return;
    const cleanDate = item.date.split("+")[0];
    const d = new Date(cleanDate);
    if (isNaN(d.getTime())) return;

    const year = d.getFullYear();
    const monthIdx = d.getMonth(); // 0 = JAN, 11 = DEC

    if (!matrix[year]) {
      matrix[year] = Array(12).fill("-");
    }
    matrix[year][monthIdx] = item.status || "-";
  });

  const years = Object.keys(matrix).sort((a, b) => b - a);
  return { years, matrix };
};

export default function ConsumerAccountDetails({ accounts = [] }) {
  // Demo fallback array if no accounts array passed
  const accountList = accounts.length > 0 ? accounts : [sampleAccountData];

  const monthsHeader = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  useEffect(() => {
    console.log("accounts", accounts);
  }, [accounts]);

  return (
    <div className="w-full max-w-5xl mx-auto p-4 bg-white custom-font text-gray-900 print:p-0">
      <h2 className="text-lg font-bold text-gray-800 mb-4 uppercase tracking-wide">
        CONSUMER ACCOUNT DETAILS
      </h2>

      {accountList.map((account, index) => {
        const tradeline = account?.Tradeline || {};
        const grantedTrade = tradeline?.GrantedTrade || {};
        const payHistory =
          grantedTrade?.PayStatusHistory?.MonthlyPayStatus || [];

        const { years, matrix } = processPaymentHistory(payHistory);

        const isOpen = !tradeline.dateClosed; // default to active if not closed

        return (
          <div key={index} className="mb-8">
            {/* ACCOUNT INDEX HEADER */}
            <h3 className="text-[13px] font-bold text-gray-800 mb-1.5 uppercase tracking-wide">
              {index + 1}. ACCOUNT
            </h3>

            {/* MAIN CARD CONTAINER */}
            <div className="border border-gray-400 rounded-[3px] overflow-hidden bg-white">
              {/* TOP INFORMATION BAR */}
              <div className="bg-white border-b border-gray-300 px-4 py-2.5 flex flex-wrap items-center justify-between relative text-[11px] font-bold">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-gray-900 uppercase tracking-wide">
                    ACCOUNT INFORMATION
                  </span>

                  <span className="text-[#00a1de] uppercase ml-2">
                    DATE OPENED :{" "}
                    <span className="text-black">
                      {formatDate(tradeline.dateOpened)}
                    </span>
                  </span>

                  <span className="text-gray-300 mx-1">|</span>

                  <span className="text-[#00a1de] uppercase">
                    DATE CLOSED :{" "}
                    <span className="text-black">
                      {formatDate(tradeline.dateClosed)}
                    </span>
                  </span>

                  <span className="text-gray-300 mx-1">|</span>

                  <span className="text-[#00a1de] uppercase">
                    DATE REPORTED & CERTIFIED :{" "}
                    <span className="text-black">
                      {formatDate(tradeline.dateReported)}
                    </span>
                  </span>
                </div>

                {/* STATUS BADGE */}
                <div
                  className={` pr-4 rounded-[2px] text-[12px] font-bold flex items-center gap-1 uppercase tracking-wider text-black `}
                >
                  {isOpen ? "ACTIVE" : "INACTIVE"}
                </div>

                <div
                  className={`absolute z-40 -top-7 text-white -right-14 w-28 rotate-45 flex justify-center items-end h-14 ${isOpen ? "bg-[#00a651]" : "bg-gray-500"}`}
                >
                  {isOpen ? (
                    <Check size={20} className="z-50 -rotate-45" />
                  ) : (
                    <X size={20} className="z-50 rotate-45" />
                  )}
                </div>
              </div>

              {/* THREE COLUMN DETAILS SECTION */}
              <div className="grid grid-cols-1 md:grid-cols-12 text-[11px] border-b border-gray-300">
                {/* COLUMN 1: ACCOUNT (4 cols) */}
                <div className="md:col-span-4 border-b md:border-b-0 md:border-r border-gray-300 p-3 space-y-2.5">
                  <div className="text-[#00a1de] font-bold uppercase tracking-wide">
                    ACCOUNT
                  </div>

                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-[#00a1de] font-bold uppercase">
                      TYPE
                    </span>
                    <span className="col-span-2 text-black font-bold uppercase">
                      : {getAccountType(account.accountTypeSymbol)}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-[#00a1de] font-bold uppercase">
                      MEMBER NAME
                    </span>
                    <span className="col-span-2 text-black font-bold uppercase">
                      : {tradeline.creditorName || "NOT DISCLOSED"}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-[#00a1de] font-bold uppercase">
                      ACCOUNT NUMBER
                    </span>
                    <span className="col-span-2 text-black font-bold uppercase">
                      : {tradeline.accountNumber || "Not Disclosed"}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-1">
                    <span className="text-[#00a1de] font-bold uppercase">
                      OWNERSHIP
                    </span>
                    <span className="col-span-2 text-black font-bold uppercase">
                      : {getOwnership(tradeline.AccountDesignator?.symbol)}
                    </span>
                  </div>
                </div>

                {/* COLUMN 2: AMOUNTS (5 cols) */}
                <div className="md:col-span-5 border-b md:border-b-0 md:border-r border-gray-300 p-3 space-y-2.5">
                  <div className="text-[#00a1de] font-bold uppercase tracking-wide">
                    AMOUNTS
                  </div>

                  <div className="grid grid-cols-12 gap-1">
                    <div className="col-span-6 flex">
                      <span className="text-[#00a1de] font-bold uppercase w-24">
                        SANCTIONED AMOUNT
                      </span>
                      <span className="text-black font-bold uppercase">
                        : {formatCurrency(tradeline.highBalance)}
                      </span>
                    </div>
                    <div className="col-span-6 flex pl-2">
                      <span className="text-[#00a1de] font-bold uppercase w-24">
                        PAYMENT FREQUENCY
                      </span>
                      <span className="text-black font-bold uppercase">
                        : {getFrequency(grantedTrade.PaymentFrequency?.symbol)}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-1">
                    <div className="col-span-6 flex">
                      <span className="text-[#00a1de] font-bold uppercase w-24">
                        CURRENT BALANCE
                      </span>
                      <span className="text-black font-bold uppercase">
                        : {formatCurrency(tradeline.currentBalance)}
                      </span>
                    </div>
                    <div className="col-span-6 flex pl-2">
                      <span className="text-[#00a1de] font-bold uppercase w-24">
                        REPAYMENT TENURE
                      </span>
                      <span className="text-black font-bold uppercase">
                        : {grantedTrade.termMonths || "-"}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-1">
                    <div className="col-span-6 flex"></div>
                    <div className="col-span-6 flex pl-2">
                      <span className="text-[#00a1de] font-bold uppercase w-24">
                        EMI
                      </span>
                      <span className="text-black font-bold uppercase">
                        : {formatCurrency(grantedTrade.EMIAmount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* COLUMN 3: STATUS (3 cols) */}
                <div className="md:col-span-3 p-3 space-y-2.5">
                  <div className="text-[#00a1de] font-bold uppercase tracking-wide">
                    STATUS
                  </div>

                  <div className="flex gap-1 leading-tight">
                    <span className="text-[#00a1de] font-bold uppercase">
                      SUIT FILED / WILFUL DEFAULT
                    </span>
                    <span className="text-black font-bold uppercase">
                      : {tradeline.suitFiledStatus || "-"}
                    </span>
                  </div>
                  <div className=" gap-1 grid grid-cols-2 align-items-center leading-tight">
                    <span className="text-[#00a1de] font-bold uppercase">
                     SETTLEMENT AMOUNT
                    </span>
                    <span className="text-black font-bold uppercase">
                      : {tradeline.settlementAmount !== "-1" ? "₹" + tradeline.settlementAmount : "₹0"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 align-items-center gap-1 leading-tight">
                    <span className="text-[#00a1de] font-bold uppercase">
                    WRITTEN OFF TOTAL AMOUNT
                    </span>
                    <span className="text-black font-bold uppercase">
                      : {tradeline.writtenOffAmtTotal !== "-1" ?  "₹" +  tradeline.writtenOffAmtTotal : "₹0"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 align-items-center gap-1 leading-tight">
                    <span className="text-[#00a1de] font-bold uppercase">
                     WRITTEN OFF PRINCIPAL
                    </span>
                    <span className="text-black font-bold uppercase">
                      : {tradeline.writtenOffPrincipal !== "-1" ?  "₹" +  tradeline.writtenOffPrincipal  : "₹0"}
                    </span>
                  </div>
                </div>
              </div>

              {/* DPD / ASSET CLASSIFICATION HEADER BAR */}
              <div className="bg-white border-b border-gray-300 px-4 py-2 flex flex-wrap items-center justify-between text-[11px] font-bold">
                <span className="text-gray-900 uppercase tracking-wide">
                  DAYS PAST DUE/ASSET CLASSIFICATION
                </span>

                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-[#00a1de] uppercase">
                    START DATE :{" "}
                    <span className="text-black">
                      {formatDate(grantedTrade.PayStatusHistory?.startDate)}
                    </span>
                  </span>
                  <span className="text-gray-300">|</span>
                  <span className="text-[#00a1de] uppercase">
                    END DATE :{" "}
                    <span className="text-black">
                      {formatDate(grantedTrade.PayStatusHistory?.endDate)}
                    </span>
                  </span>
                  <span className="text-gray-300">|</span>
                  <span className="text-[#00a1de] uppercase">
                    LAST PAYMENT :{" "}
                    <span className="text-black">
                      {formatDate(grantedTrade.dateLastPayment)}
                    </span>
                  </span>
                </div>
              </div>

              {/* MONTHLY PAYMENT MATRIX TABLE */}
              <div className="overflow-x-auto">
                <table className="w-full text-center border-collapse text-[11px]">
                  <thead>
                    <tr className="border-b border-gray-200 text-[#00a1de] font-bold bg-white">
                      <th className="py-2.5 px-3 text-left w-16">YEAR</th>
                      {monthsHeader.map((m) => (
                        <th key={m} className="py-2.5 px-1 font-bold">
                          {m}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {years.length > 0 ? (
                      years.map((year) => (
                        <tr
                          key={year}
                          className="odd:bg-[#f7f8fa] even:bg-white border-b border-gray-100 last:border-0"
                        >
                          <td className="py-2.5 px-3 text-left font-bold text-[#00a1de]">
                            {year}
                          </td>
                          {matrix[year].map((status, idx) => (
                            <td
                              key={idx}
                              className="py-2.5 px-1 font-bold text-gray-900"
                            >
                              {status}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="13"
                          className="py-4 text-gray-500 font-bold"
                        >
                          No Payment History Available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Sample fallback object based on provided JSON
const sampleAccountData = {
  accountTypeAbbreviation: "",
  accountTypeDescription: "",
  accountTypeSymbol: "06",
  Tradeline: {
    DisputeFlag: {
      symbol: "",
      description: "",
      rank: "100000",
      abbreviation: "",
    },
    creditorName: "BAJAJ FIN LTD",
    branch: "",
    highBalance: "12491",
    dateOpened: "2017-09-21+05:30",
    dateReported: "2026-07-16+05:30",
    accountsSoldTo: "",
    bureau: "",
    writtenOffAmtTotal: "4998",
    PayStatus: {
      symbol: "",
      description: "",
      rank: "100000",
      abbreviation: "",
    },
    currentBalance: "2495",
    subscriberCode: "66030001",
    AccountDesignator: {
      symbol: "1",
      description: "",
      rank: "100000",
      abbreviation: "",
    },
    dateAccountStatus: "2019-10-11+05:30",
    accountNumber: "401DPF49755385",
    AccountCondition: {
      symbol: "02",
      description: "",
      rank: "100000",
      abbreviation: "creditFacilityStatus",
    },
    Source: {
      Reference: "36f8482c-79fe-494e-9960-6aa94f75356b",
      InquiryDate: "2026-07-28+05:30",
      Locale: "en_IN",
      BorrowerKey: "426786512",
      Bureau: {
        symbol: "CIBIL",
        description: "",
        rank: "100000",
        abbreviation: "",
      },
    },
    IndustryCode: {
      symbol: "",
      description: "",
      rank: "100000",
      abbreviation: "",
    },
    thirdPartyName: "",
    GrantedTrade: {
      interestRate: "-1.00",
      PaymentFrequency: {
        symbol: "03",
        description: "",
        rank: "100000",
        abbreviation: "",
      },
      serialNumber: "934153384",
      PayStatusHistory: {
        endDate: "2023-08-01+05:30",
        startDate: "2026-07-01+05:30",
        MonthlyPayStatus: [
          { date: "2026-07-01+05:30", status: "900" },
          { date: "2026-06-01+05:30", status: "900" },
          { date: "2026-05-01+05:30", status: "900" },
          { date: "2026-04-01+05:30", status: "900" },
          { date: "2026-03-01+05:30", status: "900" },
          { date: "2026-02-01+05:30", status: "900" },
          { date: "2026-01-01+05:30", status: "900" },
          { date: "2025-12-01+05:30", status: "000" },
          { date: "2025-11-01+05:30", status: "000" },
          { date: "2025-10-01+05:30", status: "000" },
          { date: "2025-09-01+05:30", status: "000" },
          { date: "2025-08-01+05:30", status: "000" },
          { date: "2025-07-01+05:30", status: "000" },
          { date: "2025-06-01+05:30", status: "000" },
          { date: "2025-05-01+05:30", status: "000" },
          { date: "2025-04-01+05:30", status: "000" },
          { date: "2025-03-01+05:30", status: "000" },
          { date: "2025-02-01+05:30", status: "000" },
          { date: "2025-01-01+05:30", status: "000" },
          { date: "2024-12-01+05:30", status: "000" },
          { date: "2024-11-01+05:30", status: "000" },
          { date: "2024-10-01+05:30", status: "000" },
          { date: "2024-09-01+05:30", status: "000" },
          { date: "2024-08-01+05:30", status: "000" },
          { date: "2024-07-01+05:30", status: "000" },
          { date: "2024-06-01+05:30", status: "000" },
          { date: "2024-05-01+05:30", status: "000" },
          { date: "2024-04-01+05:30", status: "000" },
          { date: "2024-03-01+05:30", status: "000" },
          { date: "2024-02-01+05:30", status: "000" },
          { date: "2024-01-01+05:30", status: "000" },
          { date: "2023-12-01+05:30", status: "000" },
          { date: "2023-11-01+05:30", status: "000" },
          { date: "2023-10-01+05:30", status: "000" },
          { date: "2023-09-01+05:30", status: "000" },
          { date: "2023-08-01+05:30", status: "000" },
        ],
        status: "900,900,900...",
      },
      EMIAmount: "2499",
      WorstPayStatus: {
        symbol: "",
        description: "",
        rank: "100000",
        abbreviation: "",
      },
      CashLimit: "-1",
      dateLastPayment: "2019-10-11+05:30",
      AccountType: {
        symbol: "06",
        description: "",
        rank: "100000",
        abbreviation: "",
      },
      CreditType: {
        symbol: "06",
        description: "",
        rank: "100000",
        abbreviation: "",
      },
      actualPaymentAmount: "2499",
      termMonths: "5",
      CreditLimit: "-1",
      CollateralType: {
        symbol: "00",
        description: "",
        rank: "100000",
        abbreviation: "",
      },
      amountPastDue: "4998",
      collateral: "-1",
      TermType: {
        symbol: "",
        description: "",
        rank: "100000",
        abbreviation: "",
      },
    },
    VerificationIndicator: {
      symbol: "",
      description: "",
      rank: "100000",
      abbreviation: "",
    },
    noOfParticipants: "",
    OpenClosed: {
      symbol: "",
      description: "",
      rank: "100000",
      abbreviation: "",
    },
    position: "0",
    settlementAmount: "-1",
    writtenOffPrincipal: "4994",
  },
};
